import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { actions } from '@/lib/actions';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatLongDate } from '@/lib/format';
import { COMPOSE, useComposeContext } from '@/lib/keys';
import { closeSheet, openRecord, setRecordTab } from '@/lib/overlays';
import { forgetRecent, rememberRecent } from '@/lib/recent';
import type { RecordRef } from '@/lib/router';
import { Slot } from '@/lib/slots';
import { toastError, toast } from '@/lib/toast';
import { Callout, Empty, Skeleton } from '@/ui/bits';
import { Icon } from '@/ui/Icon';
import { BIN_DAYS } from '@shared/constants';
import { firstName, recordTab, type RecordSheet as Sheet, type RecordTab } from '@shared/record';
import type { RestoreRefusal } from '@shared/types';
import { CommentThread } from './Comments';
import { Composer, emailRecord, type ComposerMode } from './Composer';
import { CustomFields } from './CustomFields';
import { AiReview, CompanyDetails, EmailPrefs, PersonDetails } from './Details';
import { FilesBox, FilesTab } from './Files';
import { Header } from './Header';
import { History } from './History';
import { ProfileBox } from './Profile';
import { keys, useFiles, useSheet } from './queries';
import { CompanyBox, DealsBox, FollowupsBox, MeetingsBox, PeopleBox } from './SideBoxes';
import { Timeline } from './Timeline';

// The person or company sheet that slides in over any view: header, details on the left, the timeline, comments,
// history or files in the middle, and related boxes on the right.

function Loading() {
  return (
    <div className="rec-loading" aria-busy="true" aria-label="Loading the record">
      <div className="rec-band">
        <div className="rec-top">
          <span className="skel-circle" />
          <div className="rec-id">
            <Skeleton rows={3} />
          </div>
        </div>
        <div className="rec-facts" />
      </div>
      <div className="rec-grid">
        <div className="rcol left">
          <div className="tile">
            <Skeleton rows={6} />
          </div>
        </div>
        <div className="rcol">
          <div className="tile">
            <Skeleton rows={8} />
          </div>
        </div>
        <div className="rcol">
          <div className="tile">
            <Skeleton rows={4} />
          </div>
        </div>
      </div>
    </div>
  );
}

function BinNotice({ sheet }: { sheet: Sheet }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const noun = sheet.type === 'person' ? 'person' : 'company';
  const restore = async () => {
    setBusy(true);
    try {
      await api.post('/bin/restore', { type: sheet.type, ids: [sheet.id] });
      await qc.invalidateQueries({ queryKey: keys.record(sheet.type, sheet.id) });
      toast(`${sheet.name} restored`, { icon: 'restore' });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'duplicate') actions.restoreDuplicates(sheet.type, [err.details as unknown as RestoreRefusal]);
      else toastError(err);
    } finally {
      setBusy(false);
    }
  };
  const by = sheet.deletedBy ? ` by ${sheet.deletedBy.name}` : '';
  return (
    <Callout
      tone="warn"
      icon="trash"
      title={`This ${noun} is in the bin`}
      className="rec-state"
      actions={
        <button type="button" className="btn btn-sm btn-tile" onClick={() => void restore()} disabled={busy}>
          <Icon name="restore" />
          Restore
        </button>
      }
    >
      {sheet.deletedAt ? `Moved to the Bin${by} on ${formatLongDate(sheet.deletedAt)}. ` : ''}Restore it to edit it again; the Bin keeps it for {BIN_DAYS} days.
    </Callout>
  );
}

function MergedNotice({ sheet }: { sheet: Sheet }) {
  const into = sheet.mergedInto!;
  return (
    <Callout tone="info" icon="merge" title={`Merged into ${into.name}`} className="rec-state">
      Everything on this record now lives on{' '}
      <button type="button" className="link" onClick={() => openRecord(sheet.type, into.id)}>
        {into.name}
      </button>
      .
    </Callout>
  );
}

function Body({ sheet, record }: { sheet: Sheet; record: RecordRef }) {
  const tab = recordTab(record.tab);
  const live = sheet.status === 'live';
  const readOnly = !live;
  const [mode, setMode] = useState<ComposerMode>(tab === 'comments' ? 'comment' : 'note');
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const files = useFiles(sheet.type, sheet.id);

  useComposeContext(live, COMPOSE.record, () => emailRecord(sheet));

  const focusComposer = () => {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (!el) return;
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.focus({ preventScroll: true });
        el.setSelectionRange(el.value.length, el.value.length);
      }),
    );
  };

  const onTab = (next: RecordTab) => {
    if (next === tab) return;
    if (next === 'comments') setMode('comment');
    else if (mode === 'comment' && next === 'timeline') setMode('note');
    setRecordTab(next);
  };

  /** Picks a composer mode; comments live on their own tab and everything else on the timeline. */
  const onCompose = (next: ComposerMode) => {
    if (next === 'email') {
      emailRecord(sheet);
      return;
    }
    setMode(next);
    const want: RecordTab = next === 'comment' ? 'comments' : 'timeline';
    if (tab !== want) setRecordTab(want);
    focusComposer();
  };

  const onReply = (name: string) => {
    setMode('comment');
    setValue(`@${firstName(name)} `);
    focusComposer();
  };

  const composer = live ? (
    <Composer
      sheet={sheet}
      mode={tab === 'comments' ? 'comment' : mode}
      onMode={onCompose}
      inputRef={inputRef}
      value={value}
      setValue={setValue}
      canAttach={!!files.data?.storage.configured}
    />
  ) : null;

  const focus = record.focus;
  const centre =
    tab === 'comments' ? (
      <>
        {composer}
        <section className="tile" aria-labelledby={`cmts-${sheet.id}`}>
          <div className="tile-h">
            <h3 id={`cmts-${sheet.id}`}>Comments</h3>
            <span className="hint">
              <Icon name="lock" />
              Only your team sees these
            </span>
          </div>
          <CommentThread entityType={sheet.type} entityId={sheet.id} onReply={onReply} focusId={focus?.replace(/^comment:/, '')} />
        </section>
      </>
    ) : tab === 'history' ? (
      <History sheet={sheet} />
    ) : tab === 'files' ? (
      <FilesTab sheet={sheet} />
    ) : (
      <>
        {composer}
        <Timeline sheet={sheet} onReply={onReply} focus={focus} />
      </>
    );

  return (
    <>
      <Header sheet={sheet} readOnly={readOnly} tab={tab} onTab={onTab} onCompose={onCompose} />
      {sheet.status === 'bin' ? <BinNotice sheet={sheet} /> : null}
      <div className="rec-grid">
        <div className="rcol left">
          {live ? (
            <>
              <Slot name="record.fill" type={sheet.type} id={sheet.id} />
              <AiReview sheet={sheet} />
            </>
          ) : null}
          {sheet.type === 'person' ? (
            <PersonDetails sheet={sheet} readOnly={readOnly} />
          ) : (
            <>
              <ProfileBox sheet={sheet} readOnly={readOnly} />
              <CompanyDetails sheet={sheet} readOnly={readOnly} />
            </>
          )}
          <CustomFields sheet={sheet} readOnly={readOnly} />
          {sheet.type === 'person' ? <EmailPrefs sheet={sheet} readOnly={readOnly} /> : null}
        </div>
        <div className="rcol" role="tabpanel" aria-label={tab === 'timeline' ? 'Timeline' : tab === 'comments' ? 'Comments' : tab === 'history' ? 'History' : 'Files'}>
          {centre}
        </div>
        <div className="rcol">
          {sheet.type === 'person' ? (
            <>
              <DealsBox sheet={sheet} focus={focus} />
              <CompanyBox sheet={sheet} />
            </>
          ) : (
            <>
              <PeopleBox sheet={sheet} readOnly={readOnly} />
              <DealsBox sheet={sheet} focus={focus} />
            </>
          )}
          <FollowupsBox sheet={sheet} readOnly={readOnly} />
          <MeetingsBox sheet={sheet} />
          {tab === 'files' ? null : <FilesBox sheet={sheet} onAll={() => onTab('files')} />}
        </div>
      </div>
    </>
  );
}

export function RecordSheetSlot({ record }: { record: RecordRef }) {
  const q = useSheet(record.type, record.id);
  const sheet = q.data;
  const missing = q.error instanceof ApiError && q.error.status === 404;

  useEffect(() => {
    if (!sheet) return;
    if (sheet.status === 'live') {
      rememberRecent({
        kind: sheet.type,
        id: sheet.id,
        title: sheet.name,
        subtitle:
          sheet.type === 'person'
            ? [sheet.title, sheet.company?.name].filter(Boolean).join(' · ') || null
            : [sheet.industry, sheet.location].filter(Boolean).join(' · ') || null,
        hue: sheet.hue,
        ...(sheet.type === 'company' ? { logo: sheet.logo } : {}),
      });
    } else forgetRecent(sheet.type, sheet.id);
  }, [sheet?.id, sheet?.type, sheet?.status, sheet?.name, sheet?.hue, sheet?.type === 'company' ? sheet.logo : null]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (missing) forgetRecent(record.type, record.id);
  }, [missing, record.type, record.id]);

  if (q.isPending) return <Loading />;
  if (missing)
    return (
      <div className="rec-state-page">
        <Empty
          icon={record.type === 'person' ? 'user' : 'building'}
          title={record.type === 'person' ? 'Nobody at this link' : 'No company at this link'}
          action={
            <button type="button" className="btn btn-tile" onClick={closeSheet}>
              Close
            </button>
          }
        >
          The record was removed for good, or the link is incomplete. Search by name to find it.
        </Empty>
      </div>
    );
  if (q.isError || !sheet)
    return (
      <div className="rec-state-page">
        <Callout
          tone="danger"
          title="The record did not load"
          actions={
            <button type="button" className="btn btn-sm btn-tile" onClick={() => void q.refetch()}>
              <Icon name="refresh" />
              Try again
            </button>
          }
        >
          {errorMessage(q.error)}
        </Callout>
      </div>
    );
  if (sheet.status === 'merged' && sheet.mergedInto) return <MergedHeader sheet={sheet} />;
  return <Body sheet={sheet} record={record} />;
}

function MergedHeader({ sheet }: { sheet: Sheet }) {
  return (
    <>
      <Header sheet={sheet} readOnly tab="history" onTab={(t) => setRecordTab(t)} onCompose={() => undefined} />
      <MergedNotice sheet={sheet} />
      <div className="rec-merged-history">
        <History sheet={sheet} />
      </div>
    </>
  );
}
